import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import { cachedGet } from '../utils/apiCache';

const defaultNavbar = { logo_position: 'left', menu_position: 'center', shape: 'full', sticky: true, transparent: false, bg_color: '', text_color: '', hover_color: '', active_color: '', border_color: '', button_bg: '', button_text: '' };

const ThemeContext = createContext({
  activePreset: 'classic',
  variables: {},
  sliderConfig: { style: 'default', bg_color: '', text_color: '', subtitle_color: '' },
  navbarConfig: defaultNavbar,
  setTheme: () => {},
  loading: true
});

export const useTheme = () => useContext(ThemeContext);

export const ThemeProvider = ({ children }) => {
  const [activePreset, setActivePreset] = useState('classic');
  const [variables, setVariables] = useState({});
  const [sliderConfig, setSliderConfig] = useState({ style: 'default', bg_color: '', text_color: '', subtitle_color: '' });
  const [navbarConfig, setNavbarConfig] = useState(defaultNavbar);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTheme();
  }, []);

  useEffect(() => {
    if (Object.keys(variables).length === 0) return;
    const root = document.documentElement;
    Object.entries(variables).forEach(([key, value]) => {
      root.style.setProperty(key, value);
    });
  }, [variables]);

  const fetchTheme = async () => {
    try {
      const res = await cachedGet(`${API_BASE_URL}/api/theme/active`);
      setActivePreset(res.data.active_preset || 'classic');
      setVariables(res.data.variables || {});
      setSliderConfig(res.data.slider_config || { style: 'default', bg_color: '', text_color: '', subtitle_color: '' });
      setNavbarConfig(res.data.navbar_config || defaultNavbar);
    } catch {
      // Use classic defaults
    } finally {
      setLoading(false);
    }
  };

  const setTheme = async (preset, customOverrides = {}, sliderCfg = null, navbarCfg = null) => {
    try {
      const payload = {
        active_preset: preset,
        custom_overrides: customOverrides
      };
      if (sliderCfg !== null) payload.slider_config = sliderCfg;
      if (navbarCfg !== null) payload.navbar_config = navbarCfg;
      const res = await axios.put(`${API_BASE_URL}/api/theme/active`, payload, { withCredentials: true });
      if (res.data.variables) {
        setActivePreset(preset);
        setVariables(res.data.variables);
        if (res.data.slider_config) setSliderConfig(res.data.slider_config);
        if (res.data.navbar_config) setNavbarConfig(res.data.navbar_config);
      }
      return true;
    } catch {
      return false;
    }
  };

  return (
    <ThemeContext.Provider value={{ activePreset, variables, sliderConfig, navbarConfig, setTheme, loading, refetch: fetchTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};
